/**
 * GroupHeaderRow — single dense header strip for a formula group.
 *
 *   [Name — flex grow] [📅 Range] [Mode] [Layer chips] [🗑]
 *
 * Tiny icons replace word labels; the meaning is exposed via title tooltips.
 * The Automation reuses it for its ranking formulas: no layers there (chips
 * hidden), whole-day presets only, and the trash only when a range can go.
 * Per-layer group weights live in their own row below (LayerWeightsRow), not
 * here, so the header stays on one line. Wraps gracefully when narrow.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode } from 'react';
import { Calendar, Trash2 } from 'lucide-react';
import type { FlowLayer, FormulaGroupMode } from '@trafficpulse/shared';

import { Input } from '@/components/ui/form/input';
import { Button } from '@/components/ui/layout/Button';

import { FormulaGroupDateRangeDropdown } from './date-range';
import { ModeToggle } from './header';
import LayerScopeChips from './LayerScopeChips';

export interface GroupHeaderRowProps {
  name: string;
  dateRange: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  mode: FormulaGroupMode;
  /** Absent = no layer chips (a feature without layers). */
  appliesTo?: ReadonlyArray<FlowLayer>;
  onNameChange: (next: string) => void;
  onDateRangeCommit: (patch: {
    dateRange: string;
    dateRangeCustomFrom?: string;
    dateRangeCustomTo?: string;
  }) => void;
  onModeChange: (next: FormulaGroupMode) => void;
  onAppliesToChange?: (next: ReadonlyArray<FlowLayer>) => void;
  /** Absent = no trash (e.g. the last group). */
  onRemove?: () => void;
  /** Limit the date presets (the Automation: whole days only). */
  datePresets?: ReadonlyArray<string>;
  /** Route tests read today only — the control shows that, and does not open. */
  dateLocked?: boolean;
  namePlaceholder?: string;
  /** Scoping control rendered beside the layer chips (the skip-paths picker). */
  scopeControl?: ReactNode;
  /** Extra trailing actions (e.g. copy group button). */
  trailingActions?: ReactNode;
}

const IconLabel: FC<{ icon: typeof Calendar; tip: string }> = ({ icon: Icon, tip }) => (
  <span title={tip} className="text-muted-foreground/80">
    <Icon className="h-3 w-3" aria-hidden />
  </span>
);

const Divider: FC = () => (
  <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />
);

const GroupHeaderRow: FC<GroupHeaderRowProps> = ({
  name,
  dateRange,
  dateRangeCustomFrom,
  dateRangeCustomTo,
  mode,
  appliesTo,
  onNameChange,
  onDateRangeCommit,
  onModeChange,
  onAppliesToChange,
  onRemove,
  scopeControl,
  trailingActions,
  datePresets,
  dateLocked = false,
  namePlaceholder = 'Group name',
}) => (
  <div className="flex min-w-0 items-center gap-2">
    <Input
      value={name}
      onChange={(e) => onNameChange(e.target.value)}
      placeholder={namePlaceholder}
      aria-label={namePlaceholder}
      className="h-8 min-w-0 max-w-[18rem] flex-1 text-sm font-semibold"
    />
    <Divider />
    <span className="flex shrink-0 items-center gap-1">
      <IconLabel icon={Calendar} tip="Date range" />
      {dateLocked ? (
        <span className="text-[11px] font-medium" title="A route test only reads today">Today</span>
      ) : (
        <FormulaGroupDateRangeDropdown
          dateRange={dateRange}
          dateRangeCustomFrom={dateRangeCustomFrom}
          dateRangeCustomTo={dateRangeCustomTo}
          presets={datePresets}
          onCommit={onDateRangeCommit}
        />
      )}
    </span>
    <Divider />
    <ModeToggle mode={mode} onChange={onModeChange} />
    {appliesTo && onAppliesToChange ? (
      <LayerScopeChips value={appliesTo} onChange={onAppliesToChange} />
    ) : null}
    {scopeControl ? (
      <>
        <Divider />
        {scopeControl}
      </>
    ) : null}
    <span className="ml-auto flex shrink-0 items-center gap-0.5">
      {trailingActions}
      {onRemove ? (
        <Button
          size="icon"
          variant="ghost"
          type="button"
          onClick={onRemove}
          className="h-7 w-7 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
          aria-label="Remove group"
          title="Remove group"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      ) : null}
    </span>
  </div>
);

export default GroupHeaderRow;
